import { useState, useRef, useEffect, useMemo } from "react";
import {
  X,
  Users,
  Camera,
  Search,
  Check,
  UserMinus,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import { useLayout } from "@/contexts/UseLayout";
import ChatService from "@/services/ChatService";
import ConfigDesks from "@/services/configDesks";
import { toast } from "react-toastify";
import ImageGeneric from "@/assets/images/lg.png";
import { useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";

interface EditGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  room: any;
  onUpdate: () => void;
}

type GroupUserLike = {
  id?: string | number | null;
};

const getUserId = (user: GroupUserLike) => String(user?.id ?? "");

const getUsersKey = (users?: GroupUserLike[]) =>
  (users || [])
    .map(getUserId)
    .filter(Boolean)
    .sort()
    .join(",");

const getAdminIds = (ids?: Array<number | string>) =>
  (ids || [])
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
    .sort((a, b) => a - b);

export function EditGroupModal({
  isOpen,
  onClose,
  room,
  onUpdate,
}: EditGroupModalProps) {
  const { t } = useTranslation();
  const [groupName, setGroupName] = useState(room?.name || "");
  const [groupImage, setGroupImage] = useState<File | null>(null);
  const [groupImagePreview, setGroupImagePreview] = useState(
    room?.group_image || ""
  );
  const [selectedUsers, setSelectedUsers] = useState<any[]>(room?.users || []);
  const [adminIds, setAdminIds] = useState<number[]>(
    getAdminIds(room?.admin_ids)
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRoomIdRef = useRef<string | number | null>(null);
  const initializedUsersKeyRef = useRef("");
  const initializedAdminIdsKeyRef = useRef("");
  const formDirtyRef = useRef(false);
  const { userLogged } = useLayout();

  const itemsPerPage = 8;
  // Verificar se o usuário é admin
  const currentUserId = Number(userLogged?.data?.id || 0);
  const isAdmin = adminIds.includes(currentUserId);

  const { data: roomDetailsData } = useQuery({
    queryKey: ["chat_room_edit", room?.id],
    queryFn: async () => await ChatService.recuperarSala(room.id),
    enabled: isOpen && isAdmin && !!room?.id,
    retry: 1,
    staleTime: 1000 * 60,
  });

  const editRoom = roomDetailsData?.data?.room || room;
  const originalUsers = useMemo(
    () => (Array.isArray(editRoom?.users) ? editRoom.users : []),
    [editRoom?.users],
  );
  const originalGroupName = (editRoom?.name || "").trim();
  const originalUsersKey = getUsersKey(originalUsers);
  const originalAdminIds = useMemo(
    () => getAdminIds(editRoom?.admin_ids),
    [editRoom?.admin_ids],
  );
  const selectedUsersKey = getUsersKey(selectedUsers);
  const usersWereLoaded = originalUsers.length > 0;
  const usersChanged = selectedUsersKey !== originalUsersKey;
  const adminsChanged = adminIds.join(",") !== originalAdminIds.join(",");
  const originalAdminIdsKey = originalAdminIds.join(",");
  const hasEnoughParticipants = !usersWereLoaded || selectedUsers.length >= 2;
  const hasAtLeastOneAdmin = adminIds.some((adminId) =>
    selectedUsers.some((user) => Number(user.id) === adminId),
  );

  // Criar função debounced para atualizar o termo de busca
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
        setCurrentPage(1); // Reset page when search changes
      }, 500),
    []
  );

  // Query para buscar usuários disponíveis
  const {
    data: availableUsersData,
    isPending: loadingUsers,
    isFetching,
  } = useQuery({
    queryKey: ["users_chat_edit", debouncedSearchTerm, currentPage],
    queryFn: async () =>
      await ConfigDesks.getContatosBusca(
        currentPage,
        itemsPerPage,
        debouncedSearchTerm
      ),
    enabled: isOpen && isAdmin,
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Cleanup do debounce quando o componente for desmontado
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Verificar se houve alterações no grupo
  const hasChanges =
    groupName.trim() !== originalGroupName ||
    groupImage !== null ||
    usersChanged ||
    adminsChanged;

  // Filtrar usuários disponíveis (remover usuários já selecionados)
  const filteredUsers = (availableUsersData?.data?.data || []).filter(
    (user: any) => !selectedUsers.find((selected) => selected.id === user.id)
  );

  // Reset ao abrir/fechar modal
  useEffect(() => {
    if (!isOpen) {
      initializedRoomIdRef.current = null;
      initializedUsersKeyRef.current = "";
      initializedAdminIdsKeyRef.current = "";
      formDirtyRef.current = false;
      return;
    }

    const shouldInitialize =
      initializedRoomIdRef.current !== editRoom?.id ||
      (!formDirtyRef.current &&
        (initializedUsersKeyRef.current !== originalUsersKey ||
          initializedAdminIdsKeyRef.current !== originalAdminIdsKey));

    if (!shouldInitialize) {
      return;
    }

    initializedRoomIdRef.current = editRoom?.id ?? null;
    initializedUsersKeyRef.current = originalUsersKey;
    initializedAdminIdsKeyRef.current = originalAdminIdsKey;
    setGroupName(editRoom?.name || "");
    setGroupImagePreview(editRoom?.group_image || "");
    setSelectedUsers(originalUsers);
    setAdminIds(originalAdminIds);
    setGroupImage(null);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  }, [
    editRoom?.group_image,
    editRoom?.id,
    editRoom?.name,
    isOpen,
    originalUsers,
    originalUsersKey,
    originalAdminIds,
    originalAdminIdsKey,
  ]);

  // Selecionar imagem
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      formDirtyRef.current = true;
      setGroupImage(file);

      // Preview da imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        setGroupImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Adicionar usuário ao grupo
  const addUser = (user: any) => {
    formDirtyRef.current = true;
    setSelectedUsers((prev) => [...prev, user]);
  };

  // Remover usuário do grupo
  const removeUser = (userId: number) => {
    const nextUsers = selectedUsers.filter((user) => Number(user.id) !== userId);
    const nextAdminIds = adminIds.filter((adminId) => adminId !== userId);

    if (nextAdminIds.length === 0) {
      toast.error("O grupo precisa ter pelo menos um administrador.");
      return;
    }

    formDirtyRef.current = true;
    setSelectedUsers(nextUsers);
    setAdminIds(nextAdminIds);
  };

  const toggleAdmin = (userId: number) => {
    if (adminIds.includes(userId) && adminIds.length === 1) {
      toast.error("O grupo precisa ter pelo menos um administrador.");
      return;
    }

    formDirtyRef.current = true;
    setAdminIds((prev) =>
      prev.includes(userId)
        ? prev.filter((adminId) => adminId !== userId)
        : [...prev, userId],
    );
  };

  // Salvar alterações
  const handleSave = async () => {
    if (!groupName.trim()) {
      toast.error(t("chatModule.errors.group_name_required"));
      return;
    }

    if (usersWereLoaded && selectedUsers.length < 2) {
      toast.error(t("chatModule.errors.min_participants"));
      return;
    }

    if (!hasAtLeastOneAdmin) {
      toast.error("O grupo precisa ter pelo menos um administrador.");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", groupName.trim());

      if (usersWereLoaded || selectedUsers.length > 0) {
        formData.append(
          "guests",
          selectedUsers.map((user) => user.id).join(","),
        );
      }

      formData.append("admin_ids", adminIds.join(","));

      if (groupImage) {
        formData.append("group_image", groupImage);
      }

      const response = await ChatService.atualizarSala(editRoom.id, formData);

      if (response?.status === 200) {
        toast.success(t("chatModule.messages.ad_room_updated"));
        onUpdate();
        onClose();
      } else {
        toast.error(t("chatModule.errors.update_ad_room"));
      }
    } catch (error) {
      console.error("Erro ao atualizar Ad Room:", error);
      toast.error(t("chatModule.errors.update_ad_room"));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-azul-400 text-white">
          <div className="flex items-center gap-2">
            <Users size={20} />
            <h2 className="text-lg font-semibold">{t("chatModule.modal.edit_ad_room")}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-azul-500 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Verificação de permissão */}
        {!isAdmin ? (
          <div className="p-6 text-center">
            <div className="text-gray-500 mb-4">
              <Users size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t("chatModule.errors.only_admins_can_edit")}</p>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t("common:close")}
            </button>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[calc(90vh-130px)]">
            <div className="p-4 border-b">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("chatModule.modal.group_image")}
              </label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <img
                    src={groupImagePreview || ImageGeneric}
                    alt="Ad Room"
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute -bottom-1 -right-1 bg-azul-400 text-white p-1 rounded-full hover:bg-azul-500 transition-colors"
                  >
                    <Camera size={12} />
                  </button>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">
                    {t("chatModule.modal.click_camera_to_change")}
                  </p>
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>

            <div className="p-4 border-b">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("chatModule.modal.group_name")}
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => {
                  formDirtyRef.current = true;
                  setGroupName(e.target.value);
                }}
                placeholder={t("chatModule.modal.group_name_placeholder")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-400"
                maxLength={50}
              />
              <p className="text-xs text-gray-500 mt-1">
                {groupName.length}/50 {t("chatModule.modal.characters")}
              </p>
            </div>

            {/* Participantes Atuais */}
            <div className="p-4 border-b">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("chatModule.modal.participants", { count: selectedUsers.length })}
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedUsers.map((user) => {
                  const userId = Number(user.id);
                  const userIsAdmin = adminIds.includes(userId);

                  return (
                    <div
                      key={user.id}
                      className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded-lg"
                    >
                    <div className="flex items-center gap-2">
                      <img
                        src={user.url_img?.path || ImageGeneric}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">
                            {user.name} {user.last_name}
                          </p>
                          {userIsAdmin && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-azul-50 text-azul-500">
                              <ShieldCheck size={12} />
                              Admin
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleAdmin(userId)}
                        className={`p-1 rounded transition-colors ${
                          userIsAdmin
                            ? "text-amber-600 hover:bg-amber-50"
                            : "text-gray-400 hover:bg-gray-100"
                        }`}
                        title={userIsAdmin ? "Remover admin" : "Tornar admin"}
                      >
                        {userIsAdmin ? (
                          <ShieldOff size={16} />
                        ) : (
                          <ShieldCheck size={16} />
                        )}
                      </button>
                    {userId !== currentUserId && (
                      <button
                        onClick={() => removeUser(userId)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                        title={t("chatModule.modal.remove_participant")}
                      >
                        <UserMinus size={16} />
                      </button>
                    )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Adicionar Participantes */}
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("chatModule.modal.add_participants")}
              </label>

              {/* Busca */}
              <div className="relative mb-3">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={t("chatModule.modal.search_users")}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-400"
                />
                {isFetching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-azul-400"></div>
                  </div>
                )}
              </div>

              {/* Contador de resultados */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-gray-600">
                  {t("chatModule.modal.users_found", { count: availableUsersData?.data?.qtdRegistros || 0 })}
                </span>
              </div>

              {/* Lista de usuários disponíveis */}
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {loadingUsers ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-azul-400"></div>
                    <p className="text-sm text-gray-500 mt-2">
                      Carregando usuários...
                    </p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {searchTerm
                      ? t("chatModule.empty.no_users_found")
                      : t("chatModule.empty.all_users_in_group")}
                  </p>
                ) : (
                  filteredUsers.map((user: any) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                      onClick={() => addUser(user)}
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={user.filePath || ImageGeneric}
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-sm font-medium">
                            {user.name} {user.last_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {user.empresa?.nome_fantasia || user.email}
                          </p>
                        </div>
                      </div>
                      <div className="p-1 text-green-500">
                        <Check size={16} />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Paginação */}
              {!loadingUsers && filteredUsers.length > 0 && (
                <div className="flex justify-between items-center mt-3 pt-3 border-t">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50"
                  >
                    {t("common:pagination.previous")}
                  </button>
                  <span className="text-sm text-gray-600">
                    {t("common:pagination.page")} {currentPage}
                  </span>
                  <button
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={
                      (availableUsersData?.data?.qtdRegistros || 0) <=
                      currentPage * itemsPerPage
                    }
                    className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50"
                  >
                    {t("common:pagination.next")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer - Só aparece se for admin */}
        {isAdmin && (
          <div className="flex gap-2 p-4 bg-gray-50 border-t">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              {t("common:cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={
                isLoading ||
                !groupName.trim() ||
                !hasEnoughParticipants ||
                !hasAtLeastOneAdmin ||
                !hasChanges
              }
              className="flex-1 px-4 py-2 bg-azul-400 text-white rounded-lg hover:bg-azul-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t("common:saving")}
                </div>
              ) : (
                t("chatModule.modal.save_changes")
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
